import React from 'react';

import autoBind from 'react-autobind';
import Config from "../Config.jsx";
import AuthService from "./AuthService.js";
import { resolveRoute } from "./apiRoutes.js";

/**
 * API — adapter from the legacy `cmd`/`json` transport to the /api surface.
 *
 * request(cmd, reqObj, callbackOK, callbackFail) and
 * request_ans(cmd, filename, reqObj, callbackOK, callbackFail)
 * keep their EXACT signatures and callback contract, so none of the ~150 call
 * sites had to change. What changed underneath:
 *
 *   - transport: POST index.php {cmd, json} + PHPSESSID cookie
 *             -> METHOD /api/{resource}/{action} + Authorization: Bearer
 *   - envelope: {result, count, cmd, remarks}  <-  {"data": ..., "count": n}
 *
 * The negative-number error rule is preserved verbatim (see isApplicationError):
 * a response is routed to callbackOK unless `result` is a negative number, and
 * every HTTP failure is synthesised into `{result: -1, remarks: <detail>}` so
 * the existing error handling still fires (several of them read
 * `err?.body?.remarks`).
 */

// Warn once per cmd for routes that cannot express what the caller asked for.
const warnedCmds = new Set();

class API extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();

    // state variables here
    this.state = {
    };
    autoBind(this);
  }

  getAPIPath(){
    return Config.API_PATH();
  }

  render() {
      return (
        <div>

        </div>
      )
  }

  testAPIOK(res){
    // the whole return JSON object is at res.body
    console.log(res.body.result);
  }

  testAPIFail(res){
  }

  // Detect an application-level error on an HTTP-200 response.
  // A 200 carrying a failure in the body (a negative numeric or string result
  // code) must be detected here, otherwise it is wrongly routed to callbackOK.
  // NOTE: we deliberately do NOT treat a freeform `body.error` field as a
  // failure. Several endpoints handle their own success/failure via a
  // `success` flag inside callbackOK (e.g. edit_answer_score), and some may
  // return a non-empty `error` alongside valid data; globally routing those
  // to callbackFail would skip the OK handler's own recovery logic.
  isApplicationError(body){
    if (!body || typeof body !== 'object') {
      return false;
    }
    const result = body.result;
    // Numeric error code: anything below the success code (1) is a failure.
    if (typeof result === 'number') {
      return result < 0;
    }
    // String error code, e.g. "-1".
    if (typeof result === 'string') {
      const numeric = Number(result);
      if (!isNaN(numeric)) {
        return numeric < 0;
      }
    }
    // No recognizable result code (e.g. data/list endpoints): not an error.
    return false;
  }

  // ------------------------------------------------------------- internals

  /** Serialize a query object; arrays become repeated params (?ids=1&ids=2). */
  buildQuery(query) {
    if (!query) {
      return "";
    }
    const parts = [];
    Object.keys(query).forEach((key) => {
      const value = query[key];
      if (value === undefined || value === null) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== undefined && v !== null && v !== "") {
            parts.push(encodeURIComponent(key) + "=" + encodeURIComponent(v));
          }
        });
        return;
      }
      parts.push(encodeURIComponent(key) + "=" + encodeURIComponent(value));
    });
    return parts.length > 0 ? ("?" + parts.join("&")) : "";
  }

  /**
   * MySQL DATETIME columns reached the old client as "2026-07-02 23:36:07".
   * FastAPI serialises the same column as a native datetime, i.e. ISO-8601
   * "2026-07-02T23:36:07", and 51 places in this app render the value RAW —
   * so every timestamp in the UI grew a stray "T". Normalising here, at the one
   * point every response passes through, is the only change that fixes all of
   * them; the alternative is 51 call sites. Strings that merely look date-ish
   * are left alone: the pattern is anchored and requires a full ISO datetime.
   */
  static normalizeDates(value) {
    if (typeof value === 'string') {
      const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/.exec(value);
      return m ? (m[1] + ' ' + m[2]) : value;
    }
    if (Array.isArray(value)) return value.map(API.normalizeDates);
    if (value && typeof value === 'object') {
      const out = {};
      for (const k of Object.keys(value)) out[k] = API.normalizeDates(value[k]);
      return out;
    }
    return value;
  }

  /** The legacy success envelope, with the per-cmd overrides applied. */
  buildBody(cmd, descriptor, payload) {
    const raw = (payload && Object.prototype.hasOwnProperty.call(payload, 'data')) ? payload.data : payload;
    const data = API.normalizeDates(raw);
    let count = (payload && typeof payload.count === 'number') ? payload.count : null;
    if (count === null) {
      count = Array.isArray(data) ? data.length : 1;
    }

    let envelope = { result: data, count: count };
    if (descriptor && typeof descriptor.envelope === 'function') {
      envelope = descriptor.envelope(data, count);
    }

    const body = Object.assign({ result: null, count: 0 }, envelope);
    body.cmd = cmd;
    body.remarks = "";
    return body;
  }

  /** The legacy failure envelope: a NEGATIVE result, plus remarks. */
  buildErrorBody(cmd, remarks) {
    return { result: -1, count: 0, cmd: cmd, remarks: String(remarks == null ? "" : remarks) };
  }

  /** Extract FastAPI's error text ({"detail": ...}) from a failed response. */
  errorRemarks(payload, status) {
    if (payload && typeof payload === 'object') {
      const detail = payload.detail;
      if (typeof detail === 'string') {
        return detail;
      }
      if (detail !== undefined) {
        try {
          return JSON.stringify(detail);
        } catch (e) {
          // fall through
        }
      }
      // Not every failure is a FastAPI HTTPException: DELETE /api/subscription/{id}
      // answers its 409 with a hand-built {"error": ..., "assigned_schools": [...]}
      // body, and reading only `detail` turned that into a bare "HTTP 409".
      if (typeof payload.error === 'string' && payload.error) {
        return payload.error;
      }
    }
    if (typeof payload === 'string' && payload) {
      return payload;
    }
    return "HTTP " + status;
  }

  /**
   * Run one mapped cmd and resolve with the superagent-shaped response the
   * callers expect. NEVER rejects: failures come back as a response whose
   * body.result is negative, exactly like the legacy surface.
   */
  async perform(cmd, reqObj) {
    const descriptor = resolveRoute(cmd, reqObj);

    if (!descriptor) {
      console.error("[API] unmapped cmd:", cmd, reqObj);
      return { res: AuthService.makeResponse(0, this.buildErrorBody(cmd, "unmapped cmd: " + cmd)), failed: true };
    }

    if (descriptor.warn && !warnedCmds.has(cmd)) {
      warnedCmds.add(cmd);
      console.warn("[API] " + cmd + ": " + descriptor.warn);
    }

    if (descriptor.special) {
      return this.performSpecial(cmd, reqObj, descriptor);
    }

    const url = AuthService.apiUrl(descriptor.path) + this.buildQuery(descriptor.query);

    let resp = null;
    try {
      resp = await AuthService.authFetch(descriptor.method, url, descriptor.body);
    } catch (e) {
      // Network/CORS failure — no HTTP status at all.
      return { res: AuthService.makeResponse(0, this.buildErrorBody(cmd, e && e.message ? e.message : "network error")), failed: true };
    }

    let payload = null;
    try {
      const text = await resp.text();
      payload = text ? JSON.parse(text) : null;
    } catch (e) {
      payload = null;
    }

    if (!resp.ok) {
      // A descriptor may translate a designed non-2xx back into the legacy
      // 200-shaped body its caller was written against (the legacy surface
      // signalled "refused, here is why" inside a 200 envelope).
      if (typeof descriptor.onError === 'function') {
        const mapped = descriptor.onError(payload, resp.status);
        if (mapped) {
          const mappedBody = Object.assign({ result: null, count: 0 }, mapped, { cmd: cmd, remarks: "" });
          return { res: AuthService.makeResponse(resp.status, mappedBody), failed: this.isApplicationError(mappedBody) };
        }
      }
      const remarks = this.errorRemarks(payload, resp.status);
      // LoginPage.sendPasswordResetRequest tests `res.body === "recaptcha_failed"`
      // on the OK path; the route now reports it as 403 + that detail.
      if (cmd === "user_reset_password" && remarks === "recaptcha_failed") {
        return { res: AuthService.makeResponse(resp.status, "recaptcha_failed"), failed: false };
      }
      return { res: AuthService.makeResponse(resp.status, this.buildErrorBody(cmd, remarks)), failed: true };
    }

    const body = this.buildBody(cmd, descriptor, payload);
    return { res: AuthService.makeResponse(resp.status, body), failed: this.isApplicationError(body) };
  }

  /** Cmds that no single HTTP call can serve. */
  async performSpecial(cmd, reqObj, descriptor) {
    switch (descriptor.special) {
      case 'noop':
        // touch_epack: the new surface refreshes date_update inside every
        // content write, so this is a genuine server-side no-op. It must still
        // report success — 10 call sites fire it and some chain off callbackOK.
        return { res: AuthService.makeResponse(200, { result: 1, count: 0, cmd: cmd, remarks: "" }), failed: false };

      case 'logout':
        await AuthService.logout();
        return { res: AuthService.makeResponse(200, { result: 1, count: 0, cmd: cmd, remarks: "" }), failed: false };

      case 'unsupported':
        console.error("[API] " + cmd + " has no /api equivalent: " + (descriptor.reason || ""));
        return { res: AuthService.makeResponse(0, this.buildErrorBody(cmd, descriptor.reason || "unsupported cmd")), failed: true };

      case 'batch_progress':
        return this.performBatchProgress(cmd, reqObj);

      default:
        console.error("[API] unknown special handler:", descriptor.special, "for cmd", cmd);
        return { res: AuthService.makeResponse(0, this.buildErrorBody(cmd, "unknown special handler")), failed: true };
    }
  }

  /**
   * get_ai_assessment_batch_progress / get_resume_ai_assessment_batch_progress
   * are legacy-only. Rather than no-op them (which would leave AIAssessmentPage's
   * 2s poller spinning forever), the progress object is reconstructed from the
   * two answer counters the same page already polls. `failed` is not knowable
   * from counters, so it stays 0 and the batch completes when assessed >= total.
   *
   * A counter read that FAILS must never be read as "0 answers": 0/0 collapses
   * to pending 0 -> is_complete true, which told the teacher a still-running
   * (and still-billing) grading batch had finished. Any non-2xx therefore
   * propagates as a normal failure so getProgressFailed keeps polling.
   */
  async performBatchProgress(cmd, reqObj) {
    const params = {
      epack_id: reqObj ? reqObj.locale_id : undefined,
      date: reqObj ? reqObj.date : undefined,
      school_id: reqObj ? reqObj.school_id : undefined,
    };
    const query = this.buildQuery(params);

    const read = async (path) => {
      const resp = await AuthService.authFetch('GET', AuthService.apiUrl(path) + query);
      let payload = null;
      try {
        const text = await resp.text();
        payload = text ? JSON.parse(text) : null;
      } catch (e) {
        payload = null;
      }
      if (!resp.ok) {
        // Distinguish failure from emptiness — see the note above.
        const err = new Error(this.errorRemarks(payload, resp.status));
        err.status = resp.status;
        throw err;
      }
      return (payload && payload.data) ? payload.data : null;
    };

    let total = 0;
    let assessed = 0;
    try {
      const [totalData, assessedData] = await Promise.all([
        read('api/answer/totalCount'),
        read('api/answer/assessedCount'),
      ]);
      total = Number((totalData || {}).total) || 0;
      assessed = Number((assessedData || {}).total) || 0;
    } catch (e) {
      return {
        res: AuthService.makeResponse(
          (e && e.status) ? e.status : 0,
          this.buildErrorBody(cmd, e && e.message ? e.message : "network error")
        ),
        failed: true,
      };
    }

    const pending = Math.max(total - assessed, 0);
    const progress = {
      total_answers: total,
      completed_successfully: assessed,
      failed: 0,
      pending: pending,
      percentage_complete: total > 0 ? Math.round((assessed / total) * 100) : 100,
      is_complete: pending === 0,
    };
    // getProgressOK reads res.body.result.result — the legacy shape nested the
    // progress object one level deeper than the envelope.
    return {
      res: AuthService.makeResponse(200, { result: { result: progress }, count: 1, cmd: cmd, remarks: "" }),
      failed: false,
    };
  }

  // -------------------------------------------------------- public surface

  request_ans(cmd, filename, reqObj=null, callbackOK=null, callbackFail=null){
    this.perform(cmd, reqObj).then(({ res, failed }) => {
      if (failed) {
        if (callbackFail) {
          callbackFail(res);
        }
        return;
      }
      if (callbackOK) {
        // request_ans hands the OK callback a JSON STRING (RetrieverPage's
        // tidy_ans/tidy_ans_batch do JSON.parse(answers).result on it), not a
        // response object.
        callbackOK(res && typeof res.text === 'string' ? res.text : JSON.stringify(res && res.body ? res.body : {}), filename);
      }
    });
  }

  request(cmd, reqObj=null, callbackOK=null, callbackFail=null){
    this.perform(cmd, reqObj).then(({ res, failed }) => {
      if (failed) {
        if (callbackFail) {
          callbackFail(res);
        }
        return;
      }
      if (callbackOK) {
        callbackOK(res);
      }
    });
  }
}

export default API;
